"use client"

import { Box, Flex, IconButton, useMediaQuery } from "@chakra-ui/react"
import { SidebarNavClose, SidebarNavOpen } from "blx-icons"
import classNames from "classnames"
import { useHypertune } from "lib/feature-flags/generated/hypertune.react"
import dynamic from "next/dynamic"
import { usePathname } from "next/navigation"
import React from "react"
import { useOrgGroups } from "services"
import CurrentNavigation from "./components/CurrentNavigation"
import { NavigationViews, rootNavigationState } from "./state/rootNavigationState"

const OrgGroupSelectorBox = dynamic(
    () => import("features/account/accountSelection/OrgGroupSelectorBox"),
    { ssr: false }
)
const SideNavigationMenu: React.FC = () => {
    const drawer = React.useRef(null)
    const pathName = usePathname()
    const [isMaxMd] = useMediaQuery("(max-width: 1024px)")
    const { groups } = useOrgGroups()

    const navIsExpanded = rootNavigationState.use.navIsExpanded()
    const navIsHidden = rootNavigationState.use.navIsHidden()
    const showNavControls = rootNavigationState.use.showNavControls()
    const slideNavIsOpen = rootNavigationState.use.slideNavIsOpen()

    const setCurrentNavView = rootNavigationState.use.setCurrentNavView()
    const setExpandedNav = rootNavigationState.use.setExpandedNav()
    const setShowNavControls = rootNavigationState.use.setShowNavControls()
    const toggleExpandedNav = rootNavigationState.use.toggleExpandedNav()
    const toggleSideNav = rootNavigationState.use.toggleSideNav()

    const orgGroupSelectorPosition = useHypertune().orgGroupSelectorPosition({
        fallback: "bottomNav"
    })

    React.useEffect(() => {
        if (pathName.includes("dashboard")) setCurrentNavView(NavigationViews.Org)

        // This is not firing on the dashboard page when middleware sends user back
        if (!pathName.match(/pp|mmb|bm/g)) {
            setCurrentNavView(NavigationViews.Org)
            setShowNavControls(false)
        } else {
            if (pathName.includes("pp")) setCurrentNavView(NavigationViews.PP)
            if (pathName.includes("mmb")) setCurrentNavView(NavigationViews.MMB)
            if (pathName.includes("bm")) setCurrentNavView(NavigationViews.BMA)
            if (pathName.includes("brewknowledge")) setCurrentNavView(NavigationViews.BrewKnowledge)

            setShowNavControls(true)
        }

        setExpandedNav(!isMaxMd)
    }, [pathName, isMaxMd])

    const showNavToggle = React.useMemo(() => pathName !== "/dashboard", [pathName])

    React.useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (isMaxMd && navIsExpanded && drawer.current && !drawer.current.contains(e.target)) {
                toggleExpandedNav()
            }
        }

        document.addEventListener("mousedown", handleClick)
        return () => document.removeEventListener("mousedown", handleClick)
    }, [isMaxMd, navIsExpanded])

    if (navIsHidden) return null

    return (
        <Box
            ref={drawer}
            zIndex={25}
            _dark={{ bg: "gray.800" }}
            className={classNames(
                "group fixed h-full",
                slideNavIsOpen || navIsExpanded ? "w-72 max-w-72" : "lg:w-8 lg:max-w-8",
                !navIsExpanded ? "-left-px" : "left-0"
            )}
        >
            {showNavToggle && (
                <div
                    className={classNames(
                        "button-container absolute -right-[1.3rem] top-[5.25rem] z-50 transition-opacity lg:top-[6.5rem]",
                        !slideNavIsOpen && !navIsExpanded && "hidden lg:block"
                    )}
                >
                    <IconButton
                        zIndex={20}
                        size='28px'
                        className={classNames("h-7 w-7")}
                        aria-label={
                            navIsExpanded ? "Close navigation menu" : "Open navigation menu"
                        }
                        icon={navIsExpanded ? <SidebarNavClose /> : <SidebarNavOpen />}
                        borderRadius='lg'
                        onClick={(e) => {
                            e.stopPropagation()
                            toggleExpandedNav()
                        }}
                        mr={2}
                        _dark={{ borderColor: "white", color: "white", bg: "transparent" }}
                        _hover={{ _dark: { bg: "white", color: "gray.500" } }}
                    />
                </div>
            )}
            <Box
                onMouseEnter={() => {
                    !slideNavIsOpen && toggleSideNav()
                }}
                onMouseLeave={() => {
                    slideNavIsOpen && toggleSideNav()
                }}
                className={classNames(
                    navIsExpanded || slideNavIsOpen ? "w-72 max-w-72" : "max-w-0 lg:w-8 lg:max-w-8",
                    showNavControls
                        ? "border-r-gray-100  dark:border-r-gray-500"
                        : "hidden border-r-transparent lg:block",
                    "border-r-px fixed left-0 top-14 z-10 h-full max-h-full overflow-hidden bg-white md:top-16 dark:bg-gray-800 transition-all"
                )}
            >
                <div
                    className={classNames(
                        slideNavIsOpen || navIsExpanded
                            ? "flex opacity-100"
                            : "pointer-events-none w-0 opacity-0",
                        " h-full "
                    )}
                >
                    <Flex
                        flexDir='column'
                        alignItems='start'
                        justifyContent='space-between'
                        w='full'
                        h='calc(100vh - 64px)'
                        p={8}
                    >
                        <CurrentNavigation />
                        {orgGroupSelectorPosition === "bottomNav" && groups?.length > 1 && (
                            <OrgGroupSelectorBox />
                        )}
                    </Flex>
                </div>
            </Box>
        </Box>
    )
}

export default SideNavigationMenu
